import { useMutation, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, GitBranch, List as ListIcon, Plus } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { numeralDeProceso } from "@/lib/numeral"

import { DiagramaFlujo } from "./DiagramaFlujo"
import { PasoDelProceso } from "./PasoDelProceso"
import type { CargoElegible, Paso, Proceso } from "./tipos"
import { VinculosDelProceso } from "./VinculosDelProceso"

/**
 * La ficha de un proceso: cabecera, objetivo, responsable y sus pasos.
 *
 * ⚠ **Reordenar manda la lista COMPLETA.** Intercambiar dos pasos son dos escrituras
 * que pueden cruzarse con las de otra pestaña y dejar dos con el mismo número.
 */
export function FichaProceso({
  proceso,
  procesos,
  cargos,
}: {
  proceso: Proceso
  procesos: Proceso[]
  cargos: CargoElegible[]
}) {
  const clienteQuery = useQueryClient()
  const [tituloPaso, setTituloPaso] = useState("")
  const [vista, setVista] = useState<"lista" | "diagrama">("lista")

  const refrescar = () => {
    clienteQuery.invalidateQueries({ queryKey: ["procesos"] })
    // El descriptivo de cargo se calcula desde los pasos: tocar uno lo cambia.
    clienteQuery.invalidateQueries({ queryKey: ["descriptivo"] })
  }

  const cambiarProceso = useMutation({
    mutationFn: ({ id, ...datos }: { id: number } & Partial<Proceso>) =>
      api.patch(`/api/v1/procesos/${id}`, datos),
    onSuccess: refrescar,
  })

  const anadirPaso = useMutation({
    mutationFn: (id: number) => api.post(`/api/v1/procesos/${id}/pasos`, { titulo: tituloPaso }),
    onSuccess: () => {
      refrescar()
      setTituloPaso("")
    },
  })

  const cambiarPaso = useMutation({
    mutationFn: ({ id, ...datos }: { id: number } & Partial<Paso>) =>
      api.patch(`/api/v1/procesos/pasos/${id}`, datos),
    onSuccess: refrescar,
  })

  const borrarPaso = useMutation({
    mutationFn: (id: number) => api.delete(`/api/v1/procesos/pasos/${id}`),
    onSuccess: refrescar,
  })

  const reordenar = useMutation({
    mutationFn: ({ id, pasos }: { id: number; pasos: number[] }) =>
      api.put(`/api/v1/procesos/${id}/orden`, { pasos }),
    onSuccess: refrescar,
  })

  const mover = (i: number, salto: number) => {
    const ids = proceso.pasos.map((p) => p.id)
    const destino = i + salto
    const actual = ids[i]
    const vecino = ids[destino]
    if (actual === undefined || vecino === undefined) return
    ids[i] = vecino
    ids[destino] = actual
    // La lista COMPLETA, no «sube este»: ver la cabecera.
    reordenar.mutate({ id: proceso.id, pasos: ids })
  }

  const error = cambiarPaso.error ?? borrarPaso.error ?? reordenar.error ?? anadirPaso.error

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="rotulo flex-1">
          {numeralDeProceso(procesos, proceso.id)} · {proceso.nombre}
        </h2>
        {/* ⚠ Se dice con TEXTO y no sólo con el símbolo: «⊕» no significa nada
            para quien no conoce el manual, y el color tampoco puede ser el
            único portador (WCAG 1.4.1). */}
        {proceso.es_transversal && <Insignia tono="info">transversal</Insignia>}
        <button
          type="button"
          onClick={() => cambiarProceso.mutate({ id: proceso.id, activo: !proceso.activo })}
          className="boton-secundario h-8 px-2 text-xs"
        >
          {proceso.activo ? "Archivar" : "Reactivar"}
        </button>
      </div>

      {!proceso.activo && (
        <p className="mb-3 text-xs" style={{ color: "var(--fg-muted)" }}>
          Archivado: sus pasos dejan de aportar funciones a los descriptivos, pero el proceso se
          conserva — es lo que explica qué hacía quien lo ejecutaba.
        </p>
      )}

      {error && (
        <p
          role="alert"
          className="mb-3 flex items-start gap-2 text-sm"
          style={{ color: "var(--destructive)" }}
        >
          <AlertCircle size={16} aria-hidden={true} />
          {error instanceof ErrorApi ? error.message : "No se pudo guardar"}
        </p>
      )}

      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="pr-objetivo" className="mb-1 block text-xs font-medium uppercase">
            Objetivo del proceso
          </label>
          <textarea
            id="pr-objetivo"
            rows={2}
            className="campo"
            defaultValue={proceso.objetivo ?? ""}
            onBlur={(e) =>
              e.target.value !== (proceso.objetivo ?? "") &&
              cambiarProceso.mutate({ id: proceso.id, objetivo: e.target.value })
            }
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="pr-resp" className="mb-1 block text-xs font-medium uppercase">
            Responsable del proceso entero
          </label>
          {/* Distinto de responder por un paso: normalmente lo lleva quien
              coordina, y no aparece en ninguno. */}
          <select
            id="pr-resp"
            className="campo"
            value={proceso.responsable_cargo_id ?? ""}
            onChange={(e) =>
              cambiarProceso.mutate({
                id: proceso.id,
                responsable_cargo_id: e.target.value === "" ? null : Number(e.target.value),
              })
            }
          >
            <option value="">Sin responsable</option>
            {cargos.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </select>
        </div>
        <VinculosDelProceso
          proceso={proceso}
          onCambiar={(cambios) => cambiarProceso.mutate({ id: proceso.id, ...cambios })}
        />
      </div>

      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="rotulo">Pasos</h3>
        {/* ⚠ Dos vistas del MISMO dato, no dos modelos. La lista es donde se
            edita y se reordena; el diagrama es como se lee un procedimiento y
            como se explica a alguien que no lo conoce. El modelo sigue siendo
            una lista ordenada: ver la cabecera de `models/procesos.py`. */}
        <div className="flex gap-1">
          <Boton
            variante={vista === "lista" ? "primario" : "fantasma"}
            tamano="sm"
            onClick={() => setVista("lista")}
          >
            <ListIcon size={14} aria-hidden="true" />
            Lista
          </Boton>
          <Boton
            variante={vista === "diagrama" ? "primario" : "fantasma"}
            tamano="sm"
            onClick={() => setVista("diagrama")}
          >
            <GitBranch size={14} aria-hidden="true" />
            Diagrama
          </Boton>
        </div>
      </div>

      {vista === "diagrama" && <DiagramaFlujo pasos={proceso.pasos} />}

      <ul className="space-y-2" hidden={vista !== "lista"}>
        {proceso.pasos.map((paso, i) => (
          <PasoDelProceso
            key={paso.id}
            paso={paso}
            indice={i}
            total={proceso.pasos.length}
            cargos={cargos}
            onCambiar={(cambios) => cambiarPaso.mutate({ id: paso.id, ...cambios })}
            onMover={(salto) => mover(i, salto)}
            onBorrar={() => borrarPaso.mutate(paso.id)}
          />
        ))}
      </ul>

      <div className="mt-3 flex gap-2">
        <input
          className="campo flex-1"
          aria-label="Título del paso nuevo"
          placeholder="Verificar el arancel del despacho…"
          value={tituloPaso}
          onChange={(e) => setTituloPaso(e.target.value)}
        />
        <button
          type="button"
          disabled={tituloPaso.trim().length < 3 || anadirPaso.isPending}
          onClick={() => anadirPaso.mutate(proceso.id)}
          data-testid="anadir-paso"
          className="boton-primario h-9 px-3 text-sm"
        >
          <Plus size={14} aria-hidden={true} />
          Añadir paso
        </button>
      </div>
      <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
        Redáctalo como saldrá impreso: es el texto que acabará en un contrato.
      </p>
    </div>
  )
}
